import {ChangeDetectionStrategy, Component, inject, input, output} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {keyTestRowFormFields} from "../../configs/key-tests-form-fields";
import {MatFormField, MatInput, MatLabel, MatSuffix} from "@angular/material/input";
import {DecimalLimitDirective} from "../../../../../../../../shared/directives/decimal-limit.directive";
import {CdkTextareaAutosize} from "@angular/cdk/text-field";
import {KeyTestData} from "../../types/key-tests-modal.types";
import {MatButton} from "@angular/material/button";
import {KeyTestsService} from "../../services/key-tests.service";
import {DateInputDirective} from "../../../../../../../../shared/directives/date-input.directive";
import {MatDatepicker, MatDatepickerModule} from "@angular/material/datepicker";
import {MatTimepicker, MatTimepickerModule} from "@angular/material/timepicker";
import {UtilityService} from "../../../../../../../../core/services/utility.service";
import {RecordUpdateRequestParams} from "../../../../../../../../core/types/records-table.types";
import {keyTestDateFields, keyTestModalResourceType} from "../../configs/key-tests-modal-config";
import {BaseModalService} from "../../../../../../../../core/components/modals/services/base-modal.service";

@Component({
  selector: 'app-key-test-form',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    DecimalLimitDirective,
    CdkTextareaAutosize,
    MatButton,
    DateInputDirective,
    MatDatepicker,
    MatDatepickerModule,
    MatSuffix,
    MatTimepicker,
    MatTimepickerModule
  ],
  templateUrl: './key-test-form.component.html',
  styleUrl: './key-test-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class KeyTestFormComponent {
  noteId = input.required<number>()
  keyTestForm = input.required<FormGroup>();
  keyTestData = input.required<KeyTestData>();
  noFirstData = input<boolean>(false);
  isEditModeActive = input<boolean>(false);
  updateData = output<KeyTestData>();
  deleteData = output<void>();

  private keyTestService: KeyTestsService = inject(KeyTestsService);
  private utilityService: UtilityService = inject(UtilityService);
  private baseModalService: BaseModalService = inject(BaseModalService);

  public readonly keyTestRowFormFields = keyTestRowFormFields;
  public readonly minDate = new Date(Date.now() + 24 * 60 * 60 * 1000);

  public getFieldClass(): string {
    if (!this.keyTestData().id) { return ''; }

    return this.isEditModeActive() ? '' : 'none-pointer-events';
  }

  public async handleValueChange(key: string, value?: string | null): Promise<void> {
    if (!this.invalidData(key)) { return; }

    value && this.keyTestForm().get(key)?.setValue(value);
    const newValue = this.keyTestForm().get(key)?.value;

    await this.updateKeyTestField(key, newValue);
  }

  public handleDeleteData(): void {
    this.deleteData.emit();
  }

  public async handleCreateData(): Promise<void> {
    const data = this.keyTestService.setBaseKeyTestData({
      noteId: this.noteId(),
      keyTestForm: this.keyTestForm()
    });

    await this.keyTestService.createKeyTestData(data)
      .then((data) => this.updateData.emit(data));
  }

  public async handleDateChange(date: Date, key: string): Promise<void> {
    if (!this.invalidData(key)) { return; }

    const formattedDate = date ? this.utilityService.formatDateToRussianStyle(date) : null;
    await this.updateKeyTestField(key, formattedDate);
  }

  public async handleTimeChange(key: string): Promise<void> {
    if (!this.invalidData(key)) { return; }

    await Promise.resolve();
    const value = this.keyTestForm().get(key)?.value;
    const formatedTime = this.utilityService.dateToTimeString(value);
    await this.updateKeyTestField(key, formatedTime);
  }

  private invalidData(key: string): boolean {
    if (!this.keyTestData().id) { return false; }
    if (this.keyTestForm().get(key)?.invalid) {
      this.keyTestForm().get(key)?.setValue(this.keyTestData()[key]);
      return false;
    }

    return true;
  }

  private async updateKeyTestField(key: string, value: string | null): Promise<void> {
    const params = this.getRecordUpdateRequestParams(key, value);
    const data = await this.keyTestService.updateKeyTestField(params)
      .catch(() => this.convertData(this.keyTestData()));

    this.updateData.emit(data);
  }

  private getRecordUpdateRequestParams(key: string, value: string | null): RecordUpdateRequestParams {
    return {
      path: keyTestModalResourceType.path,
      recordId: this.keyTestData().id,
      fieldName: key,
      value: value
    };
  }

  private convertData(data: KeyTestData): KeyTestData {
    return this.baseModalService.convertDates<KeyTestData>(data, keyTestDateFields);
  }
}
